import React from "react";
import { ASSETS, openConsult } from "../lib/api";
import Seo, { PERSON_JSON_LD } from "../components/Seo";

const SECTIONS = [
  {
    header: "The Story",
    body: [
      "Dogs have been the constant for more than 30 years.",
      "I've worked with dogs across the USA, Australia and New Zealand \u2014 from professional canine handling with the U.S. Air Force to the pet industry and building my own pet businesses.",
      "So I know dogs. But I also know what it's like to be the person on the other end of the lead.",
    ],
    lead2: "And that's what makes Ask Kimberley different.",
    body2: [
      "You don't have to book an appointment or spend hours searching for answers.",
      "You simply message Kimberley.",
    ],
    chat: [
      "Got a dog question? Send it.",
      "Your dog won't stop barking? Message me.",
      "Your dog is bored and you're out of ideas? Ask me.",
    ],
    body3: [
      "And sometimes the answer isn't just advice. It might be a Peace Chew, a better way to keep your dog occupied, or something completely different.",
      "That's the idea behind Ask Kimberley \u2014 real dog experience, practical solutions and a real person who gets it.",
    ],
    outro: ["Your dog's bored.", "Your dog's barking.", "Ask Kimberley."],
    testid: "miles-story",
  },
];

// Real faded world map — served from /public, tinted warm via CSS mask

// Dotted flight arc from top-left to bottom-right

export default function Miles() {
  return (
    <div className="w-full bg-[#F8F2E8] text-[#1F1B18]" data-testid="miles-page">
      <Seo
        title="The Founder — Kimberley"
        description="Meet the founder of Ask Kimberley — 30+ years with dogs across the USA, Australia and New Zealand, now building a new kind of dog brand: natural products, canine expertise and digital support."
        path="/miles"
        type="profile"
        jsonLd={PERSON_JSON_LD}
      />
      {/* MAIN SPREAD */}
      <section
        className="w-full grid grid-cols-1 lg:grid-cols-12 items-stretch"
        data-testid="miles-spread"
      >
        {/* LEFT — text */}
        <div
          className="lg:col-span-7 bg-[#F8F2E8] px-6 sm:px-10 lg:px-16 xl:px-20 py-16 sm:py-20 lg:py-24"
          data-testid="miles-text-column"
        >
          <p className="font-typewriter text-xs sm:text-sm font-bold tracking-[0.4em] uppercase text-[#8B6B4A]">
            The Founder
          </p>
          <h1
            className="font-cormorant leading-[0.92] tracking-tight text-[#1F1B18] mt-6"
            style={{ fontSize: "clamp(2.75rem, 5.5vw, 5.5rem)" }}
            data-testid="miles-headline"
          >
            Dogs have been<br />
            <span className="italic text-[#D9739A]">the constant.</span>
          </h1>

          <div className="mt-14 space-y-12">
            {SECTIONS.map((s, i) => (
              <div key={s.header} data-testid={s.testid}>
                <p className="font-typewriter text-sm sm:text-base font-bold tracking-[0.32em] uppercase text-[#D9739A]">
                  {s.header}
                </p>
                {s.lead && (
                  <p className="font-cormorant italic text-2xl sm:text-3xl leading-[1.2] text-[#1F1B18] mt-5 max-w-2xl" data-testid={`${s.testid}-lead`}>
                    {s.lead}
                  </p>
                )}
                {s.leadAccent && (
                  <p className="font-cormorant italic text-2xl sm:text-3xl leading-[1.2] text-[#D9739A] mt-1 max-w-2xl">
                    {s.leadAccent}
                  </p>
                )}
                <div className="mt-4 space-y-4 max-w-2xl">
                  {s.body.map((para, idx) => (
                    <p
                      key={idx}
                      className="font-cormorant text-lg sm:text-xl leading-[1.65] text-[#1F1B18]/85"
                    >
                      {para}
                    </p>
                  ))}
                </div>
                {s.lead2 && (
                  <p className="font-cormorant italic text-2xl sm:text-3xl leading-[1.2] text-[#D9739A] mt-8 max-w-2xl" data-testid={`${s.testid}-lead2`}>
                    {s.lead2}
                  </p>
                )}
                {s.body2 && (
                  <div className="mt-6 space-y-4 max-w-2xl">
                    {s.body2.map((para, idx) => (
                      <p key={idx} className="font-cormorant text-lg sm:text-xl leading-[1.65] text-[#1F1B18]/85">{para}</p>
                    ))}
                  </div>
                )}
                {s.chat && (
                  <div className="mt-8 space-y-2 max-w-2xl border-l-2 border-[#D9739A]/40 pl-6" data-testid={`${s.testid}-chat`}>
                    {s.chat.map((line, idx) => (
                      <p key={idx} className="font-cormorant text-xl sm:text-2xl leading-[1.4] text-[#1F1B18]">
                        {line.split("? ")[0]}?{" "}
                        <span className="italic text-[#D9739A]">{line.split("? ")[1]}</span>
                      </p>
                    ))}
                  </div>
                )}
                {s.body3 && (
                  <div className="mt-8 space-y-4 max-w-2xl">
                    {s.body3.map((para, idx) => (
                      <p key={idx} className="font-cormorant text-lg sm:text-xl leading-[1.65] text-[#1F1B18]/85">{para}</p>
                    ))}
                  </div>
                )}
                {s.outro && (
                  <div className="mt-10 space-y-1 max-w-2xl" data-testid={`${s.testid}-outro`}>
                    {s.outro.map((line, idx) => (
                      <p
                        key={idx}
                        className={`font-cormorant font-semibold text-2xl sm:text-3xl leading-[1.25] ${idx === s.outro.length - 1 ? "italic text-[#D9739A]" : "text-[#1F1B18]"}`}
                      >
                        {line}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <p
            className="font-cormorant italic text-2xl sm:text-3xl text-[#1F1B18] mt-16 max-w-2xl"
            data-testid="miles-closing-line"
          >
            Started in New Zealand.
            <br />
            <span className="text-[#D9739A]">Built for dog people everywhere.</span>
          </p>

          <p
            className="font-caveat text-6xl sm:text-7xl text-[#1F1B18] mt-10"
            data-testid="miles-signature"
          >
            Kimberley
          </p>
        </div>

        {/* RIGHT — portrait */}
        <div
          className="lg:col-span-5 relative bg-[#B9BCC7] overflow-hidden min-h-[560px] lg:min-h-0"
          data-testid="miles-portrait-column"
        >
          <img
            src={ASSETS.disco_q_pink}
            alt="Ask Kimberley — the disco question mark"
            className="absolute inset-0 w-full h-[108%] object-cover object-[50%_15%]"
            data-testid="miles-portrait-image"
          />
        </div>
      </section>

      {/* CTA STRIP */}
      <section
        className="w-full bg-[#1F1B18] text-[#F8F2E8] py-16 sm:py-20 text-center px-6"
        data-testid="miles-cta-strip"
      >
        <p className="font-typewriter text-sm sm:text-base font-bold tracking-[0.45em] uppercase text-[#F8F2E8]/60">
          Partnerships &amp; Licensing
        </p>
        <h3
          className="font-cormorant italic mt-6 leading-[0.95]"
          style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}
        >
          Let's build it{" "}
          <span className="text-[#E8B8A5]">together.</span>
        </h3>
        <button
          type="button"
          onClick={() => openConsult("Partner with Us")}
          data-testid="miles-partner-cta"
          className="mt-10 inline-flex items-center justify-center bg-[#F8F2E8] text-[#1F1B18] px-10 py-4 font-typewriter text-sm sm:text-base font-bold tracking-[0.3em] uppercase hover:bg-[#D9739A] hover:text-white transition"
        >
          Partner with Us →
        </button>
      </section>

      <footer className="bg-[#1F1B18] text-[#F8F2E8] py-8 border-t border-[#F8F2E8]/10">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="font-cormorant text-lg tracking-[0.35em] uppercase">Ask Kimberley</p>
        </div>
      </footer>
    </div>
  );
}
